import { container } from "tsyringe";

// 생각해보면 factory 내에서 store 를 생성하는것이 테스트에서 용이할지는 의문이 듭니다.
// store 하나하나 구현체로서 주입되는것이 아니라면 테스트에서 좀 더 어려울 수 있다는 판단입니다.
// 따라서 현재 stateRegister 내에서 모든 server State 를 따로 등록해주고
// 이에 대한 타입을 application 내에서 사용하도록 하겠습니다. (여기서도 정의합니다. 왜냐하면 UI 에서도 사용될 수 있으니)

// impl
import StoreManagerImpl from "@/shared/libs/state-management";

// store
import useAuthStore from "@/shared/libs/stores/server/auth";
import useStudentStore from "@/shared/libs/stores/server/student";
// import useCandidateStore from "@/shared/libs/stores/server/candidate"; // Removed: migrated to useQuery
import useStudentsRecordStore from "@/shared/libs/stores/server/students-record";
import useCurriculumMetadatasStore from "@/shared/libs/stores/server/curriculum-metadatas";
import usePlannedCurriculumsStore from "@/shared/libs/stores/server/planned-curriculums";
import useLocationsStore from "@/shared/libs/stores/server/location";
import useMiddleUnitsStore from "@/shared/libs/stores/server/middle-units";
import usePendingRegistrationStudentStore from "@/shared/libs/stores/server/pending-registration-student";
import usePendingRegistrationRecordStore from "@/shared/libs/stores/server/pending-registrations-record";
import useAcademyClassesStore from "@/shared/libs/stores/server/academy-classes";
import useTeacherStore from "@/shared/libs/stores/server/teacher";
import useChallengesTemplateStore from "@/shared/libs/stores/server/challenge-template";
import useChallengesStore from "@/shared/libs/stores/server/challenge";
import useStudentChallengesStore from "@/shared/libs/stores/server/student-challenges";
import useStudentSubmittedProblemsStore from "@/shared/libs/stores/server/student-submitted-problems";
import useProblemsStore from "@/shared/libs/stores/server/problem";
import useAssignedCurriculumProblemStore from "@/shared/libs/stores/server/assigned-curriculum-problem";
import useConceptEnhancementStore from "@/shared/libs/stores/server/concept-enhancement";
// import useFormerStudentStore from "@/shared/libs/stores/server/former-student"; // Removed: migrated to useQuery

// types
import type { AuthState } from "@/shared/types/state-manager/auth";
// import type { CandidateManagerState } from "@/shared/types/state-manager/candidate"; // Removed: migrated to useQuery
import type { StudentManagerState } from "@/shared/types/state-manager/student";
import type { SearchManagerState } from "@/shared/types/state-manager/students-record";
import type { CurriculumMetadatasState } from "@/shared/types/state-manager/curriculum-metadatas";
import type { PlannedCurriculumsState } from "@/shared/types/state-manager/planned-curriculums";
import type { LocationsManagerState } from "@/shared/types/state-manager/location";
import type { MiddleUnitsManagerState } from "@/shared/types/state-manager/middle-units";
import type { PendingRegistrationStudentState } from "@/shared/types/state-manager/pending-registration-student";
import type { PendingRegistrationsRecordState } from "@/shared/types/state-manager/pending-registrations-record";
import type { AcademyClassesState } from "@/shared/types/state-manager/academy-classes";
import type { TeacherManagerState } from "@/shared/types/state-manager/teacher";
import type { ChallengeTemplateState } from "@/shared/types/state-manager/challenge-template";
import type { ChallengeState } from "@/shared/types/state-manager/challenge";
import type { StudentChallengesState } from "@/shared/types/state-manager/student-challenges";
import type { StudentSubmittedProblemsState } from "@/shared/types/state-manager/student-submitted-problems";
import type { ProblemState } from "@/shared/types/state-manager/problem";
import type { AssignedCurriculumProblemState } from "@/shared/types/state-manager/assigned-curriculum-problem";
import type { ConceptEnhancementState } from "@/shared/types/state-manager/concept-enhancement";
// import type { FormerStudentManagerState } from "@/shared/types/state-manager/former-student"; // Removed: migrated to useQuery

import type { ServerStateManager } from "@/shared/libs/stores/server/interface";

const stateRegisterInit = () => {
    container.register<ServerStateManager.AuthManager>("AuthManager", {
        useValue: new StoreManagerImpl<AuthState>(useAuthStore()),
    });
    // container.register<ServerStateManager.CandidateManager>("CandidateManager", {
    //     useValue: new StoreManagerImpl<CandidateManagerState>(useCandidateStore()),
    // }); // Removed: migrated to useQuery
    container.register<ServerStateManager.StudentManager>("StudentManager", {
        useValue: new StoreManagerImpl<StudentManagerState>(useStudentStore()),
    });
    container.register<ServerStateManager.StudentRecordManager>("StudentRecordManager", {
        useValue: new StoreManagerImpl<SearchManagerState>(useStudentsRecordStore()),
    });
    container.register<ServerStateManager.CurriculumMetadatasManager>(
        "CurriculumMetadatasManager",
        { useValue: new StoreManagerImpl<CurriculumMetadatasState>(useCurriculumMetadatasStore()) },
    );
    container.register<ServerStateManager.PlannedCurriculumsManager>("PlannedCurriculumsManager", {
        useValue: new StoreManagerImpl<PlannedCurriculumsState>(usePlannedCurriculumsStore()),
    });
    container.register<ServerStateManager.LocationsManager>("LocationsManager", {
        useValue: new StoreManagerImpl<LocationsManagerState>(useLocationsStore()),
    });
    container.register<ServerStateManager.MiddleUnitsManager>("MiddleUnitsManager", {
        useValue: new StoreManagerImpl<MiddleUnitsManagerState>(useMiddleUnitsStore()),
    });
    container.register<ServerStateManager.PendingRegistrationStudentManager>(
        "PendingRegistrationStudentManager",
        {
            useValue: new StoreManagerImpl<PendingRegistrationStudentState>(
                usePendingRegistrationStudentStore(),
            ),
        },
    );
    container.register<ServerStateManager.PendingRegistrationsRecordManager>(
        "PendingRegistrationsRecordManager",
        {
            useValue: new StoreManagerImpl<PendingRegistrationsRecordState>(
                usePendingRegistrationRecordStore(),
            ),
        },
    );
    container.register<ServerStateManager.AcademyClassesManager>("AcademyClassesManager", {
        useValue: new StoreManagerImpl<AcademyClassesState>(useAcademyClassesStore()),
    });
    container.register<ServerStateManager.TeacherManager>("TeacherManager", {
        useValue: new StoreManagerImpl<TeacherManagerState>(useTeacherStore()),
    });
    container.register<ServerStateManager.ChallengeTemplateManager>("ChallengeTemplateManager", {
        useValue: new StoreManagerImpl<ChallengeTemplateState>(useChallengesTemplateStore()),
    });
    container.register<ServerStateManager.ChallengeManager>("ChallengeManager", {
        useValue: new StoreManagerImpl<ChallengeState>(useChallengesStore()),
    });
    container.register<ServerStateManager.StudentChallengesManager>("StudentChallengesManager", {
        useValue: new StoreManagerImpl<StudentChallengesState>(useStudentChallengesStore()),
    });
    container.register<ServerStateManager.StudentSubmittedProblemsManager>(
        "StudentSubmittedProblemsManager",
        {
            useValue: new StoreManagerImpl<StudentSubmittedProblemsState>(
                useStudentSubmittedProblemsStore(),
            ),
        },
    );
    container.register<ServerStateManager.ProblemManager>("ProblemManager", {
        useValue: new StoreManagerImpl<ProblemState>(useProblemsStore()),
    });
    container.register<ServerStateManager.AssignedCurriculumProblemManager>(
        "AssignedCurriculumProblemManager",
        {
            useValue: new StoreManagerImpl<AssignedCurriculumProblemState>(
                useAssignedCurriculumProblemStore(),
            ),
        },
    );
    container.register<ServerStateManager.ConceptEnhancementManager>("ConceptEnhancementManager", {
        useValue: new StoreManagerImpl<ConceptEnhancementState>(useConceptEnhancementStore()),
    });
    // container.register<ServerStateManager.FormerStudentManager>("FormerStudentManager", {
    //     useValue: new StoreManagerImpl<FormerStudentManagerState>(useFormerStudentStore()),
    // }); // Removed: migrated to useQuery
};

export default stateRegisterInit;
